import React from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';

const PublicAgencyFilters = ({
    filters,
    handleFilterChange,
    clearFilters,
    cities,
    propertyTypes,
    t,
    i18n
}) => {
    const getPropertyTypeKey = (type) => {
        if (!type) return '';
        if (type === 'Full Building') return 'fullBuilding';
        return type.toLowerCase();
    };

    return (
        <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-surface-glass border border-white/20 backdrop-blur-xl rounded-2xl p-6 shadow-card sticky top-4">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gold/10">
                    <div className="flex items-center gap-2">
                        <FunnelIcon className="h-5 w-5 text-gold" />
                        <h3 className="text-lg font-bold text-textPrimary">{t('filters')}</h3>
                    </div>
                    <button
                        onClick={clearFilters}
                        className="text-xs text-gold hover:text-gold/80 underline transition-colors"
                    >
                        {t('clearFilters') || 'Clear'}
                    </button>
                </div>

                <div className="space-y-4">
                    {/* ID Search */}
                    <div>
                        <label className="block text-sm font-medium text-textMuted mb-2">{t('propertyId')}</label>
                        <input
                            type="text"
                            name="property_id"
                            value={filters.property_id}
                            onChange={handleFilterChange}
                            placeholder={t('searchId')}
                            className="w-full px-4 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow placeholder-textMuted/50"
                        />
                    </div>

                    {/* City */}
                    <div>
                        <label className="block text-sm font-medium text-textMuted mb-2">{t('city')}</label>
                        <select
                            name="city"
                            value={filters.city}
                            onChange={handleFilterChange}
                            className="w-full px-4 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow"
                        >
                            <option value="">{t('allCities')}</option>
                            {(cities || []).map(city => {
                                const cityValue = typeof city === 'string' ? city : city.en;
                                return (
                                    <option key={cityValue} value={cityValue}>
                                        {t(`cities.${cityValue}`, typeof city === 'string' ? cityValue : (i18n.language === 'ar' ? city.ar : city.en))}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Area */}
                    {filters.city ? (
                        <div>
                            <label className="block text-sm font-medium text-textMuted mb-2">{t('area')}</label>
                            {cities?.find(c => (typeof c === 'string' ? c : c.en) === filters.city)?.subareas?.length > 0 ? (
                                <select
                                    name="area"
                                    value={filters.area}
                                    onChange={handleFilterChange}
                                    className="w-full px-4 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow"
                                >
                                    <option value="">{t('allAreas') || 'All Areas'}</option>
                                    {cities.find(c => (typeof c === 'string' ? c : c.en) === filters.city).subareas.map((sub, index) => {
                                        const subValue = typeof sub === 'string' ? sub : sub.en;
                                        return (
                                            <option key={index} value={subValue}>
                                                {typeof sub === 'string' ? sub : (i18n.language === 'ar' ? sub.ar : (i18n.language === 'es' && sub.es ? sub.es : sub.en))}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    name="area"
                                    value={filters.area}
                                    onChange={handleFilterChange}
                                    placeholder={t('area')}
                                    className="w-full px-4 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow placeholder-textMuted/50"
                                />
                            )}
                        </div>
                    ) : null}

                    {/* Property Type */}
                    <div>
                        <label className="block text-sm font-medium text-textMuted mb-2">{t('propertyType')}</label>
                        <select
                            name="property_type"
                            value={filters.property_type}
                            onChange={handleFilterChange}
                            className="w-full px-4 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow"
                        >
                            <option value="">{t('allTypes')}</option>
                            {(propertyTypes || []).map(type => {
                                const typeValue = typeof type === 'string' ? type : type.en; // Use english key for value
                                return (
                                    <option key={typeValue} value={typeValue}>
                                        {typeof type === 'string'
                                            ? t(`propertyTypes.${getPropertyTypeKey(typeValue)}`)
                                            : (i18n.language === 'ar' ? type.ar : (i18n.language === 'es' ? type.es : type.en))}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Price Range */}
                    <div>
                        <label className="block text-sm font-medium text-textMuted mb-2">{t('priceRange')}</label>
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="number"
                                name="min_price"
                                value={filters.min_price}
                                onChange={handleFilterChange}
                                placeholder={t('min')}
                                className="w-full px-3 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow placeholder-textMuted/50"
                            />
                            <input
                                type="number"
                                name="max_price"
                                value={filters.max_price}
                                onChange={handleFilterChange}
                                placeholder={t('max')}
                                className="w-full px-3 py-2.5 bg-surface border border-gold/20 text-textPrimary rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow placeholder-textMuted/50"
                            />
                        </div>
                    </div>

                    {/* Include Sold */}
                    <div className="pt-4 border-t border-gold/10">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="include_sold"
                                id="include_sold"
                                checked={filters.include_sold}
                                onChange={handleFilterChange}
                                className="h-4 w-4 text-gold border-gold/30 rounded bg-surface focus:ring-gold"
                            />
                            <span className="ml-3 text-sm text-textMuted">{t('showSoldProperties')}</span>
                        </label>
                    </div>

                    {/* Only 3D Tours */}
                    <div className="pt-4 border-t border-gold/10">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="has_tour"
                                checked={filters.has_tour}
                                onChange={handleFilterChange}
                                className="h-4 w-4 text-gold border-gold/30 rounded bg-surface focus:ring-gold"
                            />
                            <span className="ml-3 text-sm text-textMuted">{t('only3DTours')}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicAgencyFilters;
